import { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { Alert, Button, Card, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { useCanManageAccess } from '@/access/useCanManageAccess';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import type { IssuedApiCredentialMaterial } from '@/api/models';
import { credentialStatusTone } from './domain';
import { CredentialSecretRevealPanel } from './CredentialSecretRevealPanel';
import { CredentialRotateDialog } from './CredentialRotateDialog';
import { CredentialRevokeDialog } from './CredentialRevokeDialog';
import { CredentialScopeDialog } from './CredentialScopeDialog';
import { CredentialIpAllowlistDialog } from './CredentialIpAllowlistDialog';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * API credential detail against `GET /v1/admin/api-credentials/{id}` (A3's
 * contract). `AdminApiCredential` never carries `secret`/`signingSecret` —
 * this screen's steady-state read path cannot render either value, only
 * the once-only reveal panel triggered right after Rotate can, and only for
 * the instant the admin has it open.
 */
export function CredentialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const canWrite = useCanManageAccess();
  const credentialId = id ?? '';
  const [rotateOpen, setRotateOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [ipAllowlistOpen, setIpAllowlistOpen] = useState(false);
  const [rotated, setRotated] = useState<IssuedApiCredentialMaterial>();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getAdminApiCredential(credentialId),
    credentialId,
  );

  const backButton = (
    <IconButton onClick={() => navigate('/credentials')} aria-label={translate('pay.credentials.detail.back')} size="small">
      <ArrowBackOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          {error.errorCode === 'access_denied' ? (
            <AccessDenied />
          ) : error.errorCode === 'not_found' ? (
            <EmptyState
              title={translate('pay.credentials.detail.notFoundTitle')}
              body={translate('pay.credentials.detail.notFoundBody')}
            />
          ) : (
            <ErrorState body={error.message} onRetry={reload} />
          )}
        </Card>
      </Stack>
    );
  }

  if (!data) return null;

  const isRevoked = data.status === 'revoked';

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {backButton}
        <PageTitle
          title={data.keyId}
          subtitle={data.applicationCode}
          adornment={<StatusChip label={translate(`pay.credentials.status.${data.status}`)} tone={credentialStatusTone(data.status)} />}
        />
      </Stack>

      <SectionHeader onRefresh={reload} />

      {isRevoked && <Alert severity="warning">{translate('pay.credentials.detail.revokedNotice')}</Alert>}

      <Card sx={{ p: 2.5, maxWidth: 640 }}>
        <Stack spacing={1.5}>
          <Field label={translate('pay.credentials.detail.fieldId')} value={data.id} />
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('pay.credentials.detail.fieldStatus')}
            </Typography>
            <StatusChip label={translate(`pay.credentials.status.${data.status}`)} tone={credentialStatusTone(data.status)} />
          </Stack>
          <Field
            label={translate('pay.credentials.detail.fieldScope')}
            value={
              data.allowAllTenants
                ? translate('pay.credentials.scope.allTenants')
                : data.allowedTenantCodes.length > 0
                  ? data.allowedTenantCodes.join(', ')
                  : translate('pay.credentials.scope.specificTenants')
            }
          />
          <Field
            label={translate('pay.credentials.detail.fieldIpAllowlist')}
            value={data.ipAllowlist.length > 0 ? data.ipAllowlist.join(', ') : translate('pay.credentials.detail.ipAllowlistEmpty')}
          />
          <Field
            label={translate('pay.credentials.detail.fieldRotatedAt')}
            value={data.rotatedAt ? new Date(data.rotatedAt).toLocaleString('es-ES') : translate('pay.credentials.detail.neverRotated')}
          />
          <Field label={translate('pay.credentials.detail.fieldCreatedAt')} value={new Date(data.createdAt).toLocaleString('es-ES')} />
          {canWrite && !isRevoked && (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setScopeOpen(true)}>
                {translate('pay.credentials.detail.editScopeAction')}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setIpAllowlistOpen(true)}>
                {translate('pay.credentials.detail.editIpAllowlistAction')}
              </Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => setRotateOpen(true)}>
                {translate('pay.credentials.detail.rotateAction')}
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setRevokeOpen(true)}>
                {translate('pay.credentials.detail.revokeAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>

      <CredentialScopeDialog
        open={scopeOpen}
        credential={data}
        onClose={() => setScopeOpen(false)}
        onSaved={() => {
          setScopeOpen(false);
          reload();
        }}
      />
      <CredentialIpAllowlistDialog
        open={ipAllowlistOpen}
        credential={data}
        onClose={() => setIpAllowlistOpen(false)}
        onSaved={() => {
          setIpAllowlistOpen(false);
          reload();
        }}
      />
      <CredentialRotateDialog
        open={rotateOpen}
        credentialId={data.id}
        onClose={() => setRotateOpen(false)}
        onRotated={(credential) => {
          setRotateOpen(false);
          setRotated(credential);
        }}
      />
      <CredentialRevokeDialog
        open={revokeOpen}
        credentialId={data.id}
        onClose={() => setRevokeOpen(false)}
        onRevoked={() => {
          setRevokeOpen(false);
          reload();
        }}
      />
      <CredentialSecretRevealPanel
        open={Boolean(rotated)}
        title={translate('pay.credentials.secretPanel.rotatedTitle')}
        secret={rotated?.secret}
        signingSecret={rotated?.signingSecret}
        onClose={() => {
          setRotated(undefined);
          reload();
        }}
      />
    </Stack>
  );
}
